import React from "react";
import * as I from "../Input";
import * as H from "../../../hooks";
import * as C from "../../../Common";
import * as BS from "react-bootstrap";
import { Select } from "../Components";
import { T, TC } from "../../../Constants";
import NumFields from "../Components/NumFields";

export type NEN2767Props = Omit<I.InputProps, "options"> & {
    options: ReturnType<T.API.EDL.GetNEN2767Options>;
};

type Value = T.RemarqueDefault["nen2767"];

const NEN2767: React.FC<NEN2767Props> = ({ onChange, ...props }) => {
    const lg = H.useLanguage();
    const is_disabled = React.useMemo(() => props.disabled || props.noOverride, [props.disabled, props.noOverride]);
    const value = React.useMemo<Value>(() => props.value || { gravity: undefined, intensity: undefined, amplitude: undefined, defaultRem: undefined }, [props.value]);
    const [condition, setCondition] = React.useState<React.ReactNode>(null);

    const options = React.useMemo(() => {
        const gravity = (props.options || []).filter(o => o.code === "options" && o.NENType === "gravity");
        const intensity = (props.options || []).filter(o => o.code === "options" && o.NENType === "intensity");
        const amplitude = (props.options || []).filter(o => o.code === "options" && o.NENType === "amplitude");

        const defaultRem = (props.options || []).filter(o => o.code === "rem")
            .sort((a, b) => b.nen2767_gravity - a.nen2767_gravity)
            .map(rem => ({ ...rem, label: lg.getStaticText(rem.label) }));

        return { gravity, intensity, amplitude, defaultRem };
    }, [props.options, lg]);

    const result = React.useMemo(() => {
        if (value.gravity && value.intensity && value.amplitude) {
            if (value.gravity === 1) {
                const result = Math.max(1, value.intensity - (4 - value.amplitude));
                return result;
            }
            else if (value.gravity === 2) {
                let result: number;
                if (value.intensity <= 2 && value.amplitude <= 2) result = 1;
                else result = value.intensity + value.amplitude - 3;
                return result;
            }
            else if (value.gravity === 3) {
                let result: number;
                let sum = value.intensity + value.amplitude;
                if (sum <= 3) result = 1;
                else if (sum === 4) result = 2;
                else if (sum === 5) result = 3;
                else if (sum === 6) result = 4;
                else if (sum === 7) result = 5;
                else if (sum === 8) result = 6;
                return result;
            }
        }
        else return undefined;
    }, [value.gravity, value.intensity, value.amplitude]);

    React.useEffect(() => {
        if (result !== 0 && !isNaN(result)) {
            let color = "green";
            let conditionText = "";

            if (result === 1) conditionText = lg.getStaticText(TC.MISSION_NEN2767_CONDITION_STATE1);
            else if (result === 2) conditionText = lg.getStaticText(TC.MISSION_NEN2767_CONDITION_STATE2);
            else if (result === 3) conditionText = lg.getStaticText(TC.MISSION_NEN2767_CONDITION_STATE3);
            else if (result === 4) {
                color = "orange";
                conditionText = lg.getStaticText(TC.MISSION_NEN2767_CONDITION_STATE4);
            }
            else if (result === 5 || result === 6) {
                color = "red";
                conditionText = result === 5 ? lg.getStaticText(TC.MISSION_NEN2767_CONDITION_STATE5) : lg.getStaticText(TC.MISSION_NEN2767_CONDITION_STATE6);
            }

            setCondition(
                <div style={{ color }}>
                    {lg.getStaticText(TC.MISSION_NEN2767_CONDITION)} : {conditionText}
                </div>
            );
        }
        else setCondition(null);
    }, [result]);

    const getPercentage = (amplitude: number) => {
        if (amplitude === 1) return 0;
        else if (amplitude === 2) return 2;
        else if (amplitude === 3) return 10;
        else if (amplitude === 4) return 30;
        else if (amplitude === 5) return 70;
    }

    const getAmplitude = (percentage: number) => {
        if (percentage < 2) return 1;
        else if (percentage >= 2 && percentage < 10) return 2;
        else if (percentage >= 10 && percentage < 30) return 3;
        else if (percentage >= 30 && percentage < 70) return 4;
        else if (percentage >= 70) return 5;
    }

    const render_rem = React.useCallback((option: typeof options.defaultRem[number]) => <div>
        <i className={"fa fa-circle me-2"} style={{ color: option.nen2767_gravity === 1 ? "green" : option.nen2767_gravity === 2 ? "orange" : "red" }}></i> {option.label}
    </div>, []);

    return <I.ComponentWrapper {...props} hideLabel disabled={is_disabled}>
        <BS.Accordion>
            <BS.Accordion.Item eventKey="0">
                <BS.Accordion.Header>
                    NEN2767
                </BS.Accordion.Header>
                <BS.Accordion.Body>
                    <C.Flex direction="row" justifyContent="between" alignItems="center">
                        <Select
                            label={TC.NEN2767_STANDARD_REM}
                            options={options.defaultRem}
                            value={value.defaultRem}
                            customClass="flex-grow-1"
                            typeahead={{
                                renderItem: (option) => render_rem(option as typeof options.defaultRem[number]),
                            }}
                            onChange={(defaultRem) => {
                                const defaultRems = options.defaultRem.find(o => o.value === defaultRem);
                                onChange?.({ ...value, defaultRem, gravity: defaultRems?.nen2767_gravity, intensity: defaultRems?.nen2767_intensity });
                            }}
                        />
                    </C.Flex>
                    <C.Flex direction="row" justifyContent="between" alignItems="center">
                        <Select
                            label={TC.MISSION_NEN2767_GRAVITY_LABEL}
                            options={options.gravity}
                            value={value.gravity}
                            customClass="flex-grow-2"
                            onChange={(gravity) => onChange?.({ ...value, gravity })}
                        />
                        <Select
                            label={TC.MISSION_NEN2767_INTENSITY_LABEL}
                            options={options.intensity}
                            value={value.intensity}
                            customClass="flex-grow-2 mx-2"
                            onChange={(intensity) => onChange?.({ ...value, intensity })}
                        />
                        <C.Flex direction="row" className="flex-grow-1 w-25 align-items-center" >
                            <NumFields
                                label={TC.MISSION_NEN2767_PERCENTAGE}
                                customClass="w-50 me-2"
                                value={value.percentage}
                                suffix="%"
                                onChange={(percentage: number) => {
                                    const bounded = Math.min(Math.max(percentage, 0), 100);
                                    onChange?.({ ...value, percentage: bounded, amplitude: getAmplitude(bounded) });
                                }}
                            />
                            <Select
                                label={TC.MISSION_NEN2767_AMPL_LABEL}
                                options={options.amplitude}
                                value={value.amplitude}
                                customClass="w-50"
                                onChange={(amplitude) => {
                                    onChange?.({ ...value, amplitude, percentage: getPercentage(amplitude) });
                                }}
                            />
                        </C.Flex>

                    </C.Flex>
                    {condition}
                </BS.Accordion.Body>
            </BS.Accordion.Item>
        </BS.Accordion>
    </I.ComponentWrapper>;
}

export default NEN2767;